import React, { useState } from "react";
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Backdrop,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useUserQuery } from "../../../../hooks/api_hooks/useUserQuery";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";

/* ─── Helper ─── */
function formatDate(unixSeconds) {
  const ts = Number(unixSeconds);
  if (!Number.isFinite(ts)) return "—";
  const d = new Date(ts * 1000);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
}

/* ─── Stat row ─── */
function StatRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.5,
        borderBottom: "1px solid #F8FAFC",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ fontSize: "0.825rem", color: "#94A3B8" }}>
        {label}
      </Typography>
      <Typography
        sx={{ fontSize: "0.825rem", fontWeight: 600, color: "#0F172A" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/* ─── Action row ─── */
function ActionRow({
  label,
  description,
  buttonLabel,
  buttonColor,
  borderColor,
  hoverBg,
  onClick,
  disabled,
  icon,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        py: 1.75,
        borderBottom: "1px solid #F8FAFC",
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box>
        <Typography
          sx={{ fontSize: "0.825rem", fontWeight: 600, color: "#0F172A" }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "#94A3B8", mt: 0.25 }}>
          {description}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        disabled={disabled}
        onClick={onClick}
        startIcon={icon}
        sx={{
          color: buttonColor,
          borderColor: borderColor,
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "none",
          borderRadius: "8px",
          px: 2,
          py: 0.6,
          flexShrink: 0,
          ml: 2,
          boxShadow: "none",
          "&:hover": {
            borderColor: borderColor,
            backgroundColor: hoverBg,
            boxShadow: "none",
          },
          "&:disabled": { opacity: 0.4 },
        }}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
}

const Subscription = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useUserQuery({ keepPreviousData: true });

  const [isEditing, setIsEditing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [banner, setBanner] = useState(null);

  const isRedirecting = React.useRef(false);

  const accessToken = user?.access_token ?? null;

  /* ─── Mutations ─── */
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("https://api.kloudscan.com/subscription", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["user"], (prev) =>
        prev
          ? {
              ...prev,
              user: {
                ...prev.user,
                stripe_data: {
                  ...prev.user.stripe_data,
                  subscription: result.stripe_data?.subscription || "inactive",
                  plan: result.stripe_data?.plan,
                  start_date: result.stripe_data?.start_date,
                  next_payment_date: result.stripe_data?.next_payment_date,
                  access_until: result.stripe_data?.access_until,
                  cancel_at_period_end:
                    result.stripe_data?.cancel_at_period_end,
                },
              },
            }
          : prev,
      );
      setBanner({ type: "success", message: "Subscription canceled." });
      setConfirmDialog(null);
      setIsEditing(false);
      queryClient.refetchQueries({ queryKey: ["user"], exact: true });
    },
    onError: (err) => setBanner({ type: "error", message: err.message }),
  });

  const upgradeMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("https://api.kloudscan.com/subscription", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ upgrade: true }),
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.setQueryData(["user"], (prev) =>
        prev
          ? {
              ...prev,
              user: {
                ...prev.user,
                stripe_data: {
                  ...prev.user.stripe_data,
                  subscription: result.stripe_data?.subscription || "active",
                  plan: result.stripe_data?.plan,
                  start_date: result.stripe_data?.start_date,
                  next_payment_date: result.stripe_data?.next_payment_date,
                  access_until: null,
                  cancel_at_period_end: false,
                },
              },
            }
          : prev,
      );
      setBanner({ type: "success", message: "Upgraded to annual plan!" });
      setConfirmDialog(null);
      setIsEditing(false);
      queryClient.refetchQueries({ queryKey: ["user"], exact: true });
    },
    onError: (err) => setBanner({ type: "error", message: err.message }),
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        "https://api.kloudscan.com/stripe_update_billing",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    onSuccess: ({ url }) => {
      isRedirecting.current = true;
      window.location.href = url;
    },
    onError: (err) => setBanner({ type: "error", message: err.message }),
  });

  const isMutating =
    cancelMutation.isPending ||
    upgradeMutation.isPending ||
    updatePaymentMutation.isPending ||
    isRedirecting.current;

  /* ─── Loading ─── */
  if (isLoading && !data?.user) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress size={28} sx={{ color: "#f59e0b" }} />
      </Box>
    );
  }

  /* ─── Derived state ─── */
  const stripeData = data?.user?.stripe_data ?? {};
  const subStatus = stripeData.subscription ?? "inactive";
  const now = Math.floor(Date.now() / 1000);
  const rawAccessUntil = stripeData.access_until
    ? Number(stripeData.access_until)
    : null;
  const rawNextPayment = stripeData.next_payment_date
    ? Number(stripeData.next_payment_date)
    : null;

  // Canceled but still has access
  const isCancellationPending =
    stripeData.cancel_at_period_end === true &&
    rawAccessUntil &&
    rawAccessUntil > now;

  // Fully active (not canceled)
  const isActive = subStatus === "active" && !isCancellationPending;

  // Fully expired — had a subscription before but it's done
  const isExpired =
    subStatus === "inactive" &&
    !isCancellationPending &&
    !!stripeData.customer_id;

  const plan = stripeData.plan ?? "unknown";

  const planDisplay =
    plan === "year"
      ? "Standard — Annual"
      : plan === "month"
        ? "Standard — Monthly"
        : "Standard";

  const startDateStr = formatDate(stripeData.start_date);
  const nextPaymentStr = formatDate(rawNextPayment);
  const accessUntilStr = rawAccessUntil ? formatDate(rawAccessUntil) : "—";

  const handleActionConfirm = (type) => {
    if (type === "cancel") cancelMutation.mutate();
    if (type === "upgrade") upgradeMutation.mutate();
  };

  /* ─── Status indicator ─── */
  const statusColor = isCancellationPending
    ? "#f59e0b"
    : isActive
      ? "#22c55e"
      : "#94A3B8";
  const statusLabel = isCancellationPending
    ? "Cancels soon"
    : isActive
      ? "Active"
      : "Inactive";

  return (
    <>
      {/* ─── Banner ─── */}
      <Snackbar
        open={!!banner}
        autoHideDuration={3500}
        onClose={() => setBanner(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        {banner && (
          <Alert
            severity={banner.type}
            variant="filled"
            sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.875rem" }}
          >
            {banner.message}
          </Alert>
        )}
      </Snackbar>

      {/* ─── Backdrop ─── */}
      <Backdrop
        open={isMutating}
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.modal + 1,
          backgroundColor: "rgba(0,0,0,0.4)",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress sx={{ color: "#f59e0b" }} />
        </Stack>
      </Backdrop>

      {/* ─── Card ─── */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid #F1F5F9",
            backgroundColor: "#FAFBFD",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#0F172A" }}
            >
              Subscription
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", color: "#94A3B8", mt: 0.25 }}
            >
              Plan, billing, and status
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Status dot */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: statusColor,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748B" }}
              >
                {statusLabel}
              </Typography>
            </Box>

            {/* Resubscribe — only when fully expired */}
            {isExpired && !isEditing && (
              <Button
                onClick={() => navigate("/checkout")}
                startIcon={<RefreshIcon sx={{ fontSize: "14px !important" }} />}
                size="small"
                sx={{
                  backgroundColor: "#f59e0b",
                  color: "#1a0a00",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 1.75,
                  py: 0.6,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#d97706", boxShadow: "none" },
                }}
              >
                Resubscribe
              </Button>
            )}

            {/* Edit — always show */}
            {!isEditing && (
              <Button
                onClick={() => setIsEditing(true)}
                startIcon={<EditIcon sx={{ fontSize: "14px !important" }} />}
                size="small"
                sx={{
                  backgroundColor: isExpired ? "#fff" : "#f59e0b",
                  color: isExpired ? "#64748B" : "#1a0a00",
                  border: isExpired ? "1px solid #E2E8F0" : "none",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 1.75,
                  py: 0.6,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isExpired ? "#F8FAFC" : "#d97706",
                    boxShadow: "none",
                  },
                }}
              >
                Edit
              </Button>
            )}

            {isEditing && (
              <Typography
                sx={{ fontSize: "0.75rem", fontWeight: 500, color: "#94A3B8" }}
              >
                Editing
              </Typography>
            )}
          </Box>
        </Box>

        {/* ── Read-only view ── */}
        {!isEditing && (
          <Box sx={{ px: 3, py: 0.5 }}>
            <StatRow label="Plan" value={planDisplay} />
            <StatRow label="Member since" value={startDateStr} />
            {isCancellationPending ? (
              <StatRow label="Access until" value={accessUntilStr} />
            ) : isActive ? (
              <StatRow label="Next payment" value={nextPaymentStr} />
            ) : (
              <StatRow label="Next payment" value="—" />
            )}
          </Box>
        )}

        {/* ── Edit view ── */}
        {isEditing && (
          <Box sx={{ px: 3, py: 0.5 }}>
            <StatRow label="Plan" value={planDisplay} />
            {isCancellationPending ? (
              <StatRow label="Access until" value={accessUntilStr} />
            ) : isActive ? (
              <StatRow label="Next payment" value={nextPaymentStr} />
            ) : (
              <StatRow label="Next payment" value="—" />
            )}

            <Box sx={{ borderTop: "1px solid #F1F5F9", mt: 0.5 }} />

            <ActionRow
              label="Update payment method"
              description="Change your card details"
              buttonLabel="Update"
              buttonColor="#0369a1"
              borderColor="#7dd3fc"
              hoverBg="#f0f9ff"
              disabled={isMutating}
              onClick={() => updatePaymentMutation.mutate()}
              icon={<CreditCardIcon sx={{ fontSize: "14px !important" }} />}
            />

            {/* Only show upgrade/cancel if fully active */}
            {isActive && (
              <>
                {plan === "month" && (
                  <ActionRow
                    label="Upgrade to annual"
                    description="Save compared to monthly billing"
                    buttonLabel="Upgrade"
                    buttonColor="#16a34a"
                    borderColor="#86efac"
                    hoverBg="#f0fdf4"
                    disabled={isMutating}
                    onClick={() => setConfirmDialog("upgrade")}
                    icon={
                      <ArrowUpwardIcon sx={{ fontSize: "14px !important" }} />
                    }
                  />
                )}

                <ActionRow
                  label="Cancel subscription"
                  description="Access continues until end of billing period"
                  buttonLabel="Cancel"
                  buttonColor="#dc2626"
                  borderColor="#fca5a5"
                  hoverBg="#fef2f2"
                  disabled={isMutating}
                  onClick={() => setConfirmDialog("cancel")}
                  icon={<BlockIcon sx={{ fontSize: "14px !important" }} />}
                />
              </>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                py: 2,
                borderTop: "1px solid #F1F5F9",
                mt: 0.5,
              }}
            >
              <Button
                onClick={() => setIsEditing(false)}
                startIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
                sx={{
                  color: "#64748B",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  borderRadius: "8px",
                  px: 2,
                  border: "1px solid #E2E8F0",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#F8FAFC",
                    borderColor: "#CBD5E1",
                    boxShadow: "none",
                  },
                }}
              >
                Done Editing
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ─── Confirm Dialog ─── */}
      <Dialog
        open={!!confirmDialog}
        onClose={isMutating ? undefined : () => setConfirmDialog(null)}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
            px: 1,
            py: 0.5,
            minWidth: 340,
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: 700, fontSize: "1rem", color: "#0F172A", pb: 1 }}
        >
          {confirmDialog === "cancel"
            ? "Cancel subscription?"
            : "Upgrade to annual?"}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ fontSize: "0.875rem", color: "#64748B", lineHeight: 1.6 }}
          >
            {confirmDialog === "cancel"
              ? "You'll keep access until the end of your current billing period. This cannot be undone."
              : "Switch to annual billing and save compared to monthly. Takes effect immediately."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            disabled={isMutating}
            onClick={() => setConfirmDialog(null)}
            sx={{
              color: "#64748B",
              fontWeight: 600,
              fontSize: "0.82rem",
              textTransform: "none",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              px: 2,
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            Go back
          </Button>
          <Button
            variant="contained"
            disabled={isMutating}
            onClick={() => handleActionConfirm(confirmDialog)}
            sx={{
              backgroundColor:
                confirmDialog === "cancel" ? "#dc2626" : "#16a34a",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.82rem",
              textTransform: "none",
              borderRadius: "8px",
              px: 2.5,
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  confirmDialog === "cancel" ? "#b91c1c" : "#15803d",
                boxShadow: "none",
              },
            }}
          >
            {isMutating ? (
              <CircularProgress size={14} sx={{ color: "inherit" }} />
            ) : confirmDialog === "cancel" ? (
              "Yes, cancel"
            ) : (
              "Yes, upgrade"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Subscription;
